import React from 'react'
import {
  Box,
  enqueueSnackbar,
  Typography,
  Tooltip,
  IconButton,
} from '@applift/factor'
import { ArrowsMinimize, ArrowsMaximize, Download, Audio } from '@applift/icons'
import { parseVASTDAAST } from 'iqm-framework'
import { min, unescape } from 'lodash'
import Axios from 'axios'

import {
  NATIVE_CREATIVE_ID,
  HTML_CREATIVE_ID,
  IMAGE_CREATIVE_ID,
  AUDIO_CREATIVE_ID,
  VIDEO_CREATIVE_ID,
} from '../../../../constants/creative'
import {
  downloadBlob,
  getFileExtension,
  getMimeType,
} from '../../../../utils/index'
import { CreativeInfo } from '../../../../models/Creative'
import { PreviewFooter } from '../CreativePreviewFooter/index'

import styles from './styles.module.scss'

interface Props {
  creativeDetails: CreativeInfo | null
}

const PREVIEW_HEIGHT = 334
const PREVIEW_WIDTH = 500

export const CreativeVisualPreview = (props: Props) => {
  const { creativeDetails } = props

  const [displayActualSize, setDisplayActualSize] = React.useState(true)
  const [allowSizeChange, setAllowSizeChange] = React.useState(false)
  const [creativeData, setCreativeData] = React.useState<string | null>(null)
  const [downloadableCreative, setDownloadableCreative] = React.useState<
    string | null
  >(null)
  const [isVideoAudioLoadFailed, setIsVideoAudioLoadFailed] =
    React.useState(false)
  const [hasClickedAudio, setHasClickedAudio] = React.useState(false)

  const iframeRefFitted = React.useRef<any | null>(null)
  const iframeRefActualSize = React.useRef<any | null>(null)

  const isNativeVideo = React.useMemo(
    () =>
      creativeDetails?.creativeTypeId === NATIVE_CREATIVE_ID &&
      typeof creativeDetails.duration === 'number',
    [creativeDetails]
  )
  const isNative = React.useMemo(() => {
    return creativeDetails?.creativeTypeId === NATIVE_CREATIVE_ID
  }, [creativeDetails])

  React.useEffect(() => {
    setCreativeData(null)
    setDownloadableCreative(null)
    setAllowSizeChange(!isNative)
    setDisplayActualSize(!isNative)
    setIsVideoAudioLoadFailed(false)
    setHasClickedAudio(false)
    iframeRefFitted.current = null
    iframeRefActualSize.current = null
    const decoded = unescape(creativeDetails?.creativeSource || '')

    switch (creativeDetails?.creativeTypeId) {
      case HTML_CREATIVE_ID:
        setCreativeData(decoded)
        setDownloadableCreative(`data:text/html,${decoded}`)
        if (
          creativeDetails.height &&
          creativeDetails.width &&
          (creativeDetails.width > PREVIEW_WIDTH ||
            creativeDetails.height > PREVIEW_HEIGHT)
        ) {
          setAllowSizeChange(true)
        }
        break
      case IMAGE_CREATIVE_ID:
        setCreativeData(unescape(creativeDetails.creativeSource))
        setDownloadableCreative(creativeDetails.creativeSourceURL as string)
        if (
          creativeDetails.height &&
          creativeDetails.width &&
          (creativeDetails.width > PREVIEW_WIDTH ||
            creativeDetails.height > PREVIEW_HEIGHT)
        ) {
          setAllowSizeChange(true)
        }
        break
      case NATIVE_CREATIVE_ID:
        if (isNativeVideo) {
          parseVASTDAAST(creativeDetails?.creativeSource).then(
            (mediaUrl: string) => {
              setCreativeData(mediaUrl)
              setDownloadableCreative(mediaUrl)
            }
          )
        } else {
          setCreativeData(creativeDetails.creativeSource)
          setDownloadableCreative(creativeDetails.creativeSourceURL as string)
        }
        if (
          creativeDetails.height &&
          creativeDetails.width &&
          (creativeDetails.width > PREVIEW_WIDTH ||
            creativeDetails.height > PREVIEW_HEIGHT)
        ) {
          setAllowSizeChange(true)
        }
        break
      case AUDIO_CREATIVE_ID:
      case VIDEO_CREATIVE_ID:
        parseVASTDAAST(creativeDetails?.creativeSource).then(
          (mediaUrl: string) => {
            setCreativeData(mediaUrl)
            setDownloadableCreative(mediaUrl)
          }
        )
        setAllowSizeChange(false)
        break
      default:
        setCreativeData(null)
        setDownloadableCreative(null)
    }
  }, [creativeDetails, isNative, isNativeVideo])

  const writeHtml = (ref: any, isFitted: boolean) => {
    if (isFitted) {
      iframeRefFitted.current = ref
    } else {
      iframeRefActualSize.current = ref
    }

    if (!ref || !creativeDetails) {
      return
    }

    const iframeDoc = ref.contentDocument
    iframeDoc.open()
    iframeDoc.write(unescape(creativeDetails.creativeSource))
    iframeDoc.close()

    const head = iframeDoc.querySelector('head')
    const style = document.createElement('style')
    head.appendChild(style)
    style.appendChild(
      document.createTextNode(
        'body { margin: 0 !important; overflow: hidden; }'
      )
    )
  }

  const getVideoAudioError = (isVideo: boolean) => {
    return (
      <Box className={styles.videoAudioLoadError}>
        <Typography sx={{ textColor: 'neutral-0' }}>{`${
          isVideo ? 'Video' : 'Audio'
        } Loading Failed`}</Typography>
      </Box>
    )
  }

  const renderPreview = React.useMemo(() => {
    if (!creativeDetails) {
      return null
    }
    const { width, height } = creativeDetails || {}
    switch (creativeDetails?.creativeTypeId) {
      case HTML_CREATIVE_ID:
      case IMAGE_CREATIVE_ID:
        return width && height && allowSizeChange ? (
          <>
            <iframe
              title="actual_size_creative_preview"
              ref={(ref: any) => writeHtml(ref, false)}
              frameBorder="0"
              className={`${styles.actualSizePreview} ${styles.previewItem}`}
              width={width}
              height={height}
              style={{
                width: `${width}px`,
                height: `${height}px`,
                minWidth: `${width}px`,
                minHeight: `${height}px`,
              }}
            />
            <iframe
              title="fitted_size_creative_preview"
              ref={(ref: any) => writeHtml(ref, true)}
              frameBorder="0"
              className={`${styles.fittedPreview} ${styles.previewItem}`}
              width={width}
              height={height}
              style={{
                transform: `scale(${min([
                  PREVIEW_WIDTH / width,
                  PREVIEW_HEIGHT / height,
                ])}) translate(-50%, -50%)`,
                width: `${width}px`,
                height: `${height}px`,
              }}
            />
          </>
        ) : (
          <iframe
            title="actual_size_creative_preview"
            ref={(ref: any) => writeHtml(ref, false)}
            frameBorder="0"
            width={width || PREVIEW_WIDTH}
            height={height || PREVIEW_HEIGHT}
            className={`${styles.actualSizePreview} ${styles.previewItem}`}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              minWidth: `${width}px`,
              minHeight: `${height}px`,
            }}
          />
        )
      case NATIVE_CREATIVE_ID:
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: 100,
              mx: 'auto',
              pb: 16,
            }}
          >
            {!isNativeVideo ? (
              <Box
                sx={{ mx: 'auto', width: 100 }}
                style={{ height: PREVIEW_HEIGHT - 70 }}
              >
                {width && height && allowSizeChange ? (
                  <>
                    <iframe
                      title="actual_size_creative_preview"
                      ref={(ref: any) => writeHtml(ref, false)}
                      frameBorder="0"
                      className={`${styles.actualSizePreview} ${styles.previewItem}`}
                      width={width}
                      height={height}
                      style={{
                        width: `${width}px`,
                        height: `${height}px`,
                        minWidth: `${width}px`,
                        minHeight: `${height}px`,
                      }}
                    />
                    <iframe
                      title="fitted_size_creative_preview"
                      ref={(ref: any) => writeHtml(ref, true)}
                      frameBorder="0"
                      className={`${styles.fittedPreview} ${
                        styles.previewItem
                      } ${isNative ? styles.nativeFittedPreview : ''}`}
                      width={width}
                      height={height}
                      style={{
                        transform: `scale(${min([
                          PREVIEW_WIDTH / width,
                          PREVIEW_HEIGHT / height,
                        ])}) translate(-50%, -50%)`,
                        width: `${width}px`,
                        height: `${height}px`,
                      }}
                    />
                  </>
                ) : (
                  <iframe
                    title="actual_size_creative_preview"
                    ref={(ref: any) => writeHtml(ref, false)}
                    frameBorder="0"
                    width={width || PREVIEW_WIDTH}
                    height={height || PREVIEW_HEIGHT}
                    className={`${styles.actualSizePreview} ${styles.previewItem} `}
                    style={{
                      width: `${width}px`,
                      height: `${height}px`,
                      minWidth: `${width}px`,
                      minHeight: `${height}px`,
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box className={styles.videoPreviewWrapper}>
                {isVideoAudioLoadFailed ? (
                  getVideoAudioError(true)
                ) : (
                  // eslint-disable-next-line
                  <video
                    className={styles.videoPreview}
                    src={creativeData || ''}
                    controls
                    onError={() => setIsVideoAudioLoadFailed(true)}
                  />
                )}
              </Box>
            )}
            <PreviewFooter
              creativeDetails={creativeDetails}
              displayActualSize={displayActualSize}
            />
          </Box>
        )
      case VIDEO_CREATIVE_ID:
        if (creativeData === null) {
          return null
        }
        return (
          <Box className={styles.videoPreviewWrapper}>
            {isVideoAudioLoadFailed ? (
              getVideoAudioError(true)
            ) : (
              // eslint-disable-next-line
              <video
                className={styles.videoPreview}
                src={creativeData || ''}
                controls
                onError={() => setIsVideoAudioLoadFailed(true)}
              />
            )}
          </Box>
        )
      case AUDIO_CREATIVE_ID:
        if (creativeData === null) {
          return null
        }

        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              height: 100,
              width: 100,
            }}
          >
            {hasClickedAudio && !isVideoAudioLoadFailed ? (
              <Box
                sx={{
                  my: 'auto',
                }}
              >
                {/* eslint-disable-next-line */}
                <audio
                  controls
                  src={creativeData || ''}
                  className={styles.audioPlayer}
                  autoPlay
                  onError={() => setIsVideoAudioLoadFailed(true)}
                />
              </Box>
            ) : (
              <Box
                onClick={
                  creativeData ? () => setHasClickedAudio(true) : undefined
                }
                sx={{ my: 'auto' }}
                style={{ cursor: 'pointer' }}
              >
                {isVideoAudioLoadFailed ? (
                  getVideoAudioError(false)
                ) : (
                  <Audio
                    className={styles.audioDefaultIcon}
                    fontSize={80}
                    sx={{ textColor: 'neutral-500' }}
                  />
                )}
              </Box>
            )}
          </Box>
        )
      default:
        return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creativeDetails,
    allowSizeChange,
    creativeData,
    isVideoAudioLoadFailed,
    hasClickedAudio,
  ])

  const handleMediaDownload = React.useCallback(
    async (url: string, fileName: string) => {
      try {
        enqueueSnackbar(
          'The file download will begin shortly. Please wait...',
          {
            variant: 'success',
          }
        )

        const fileExtension = getFileExtension(url)
        const mimeType = getMimeType(fileExtension || '')

        const blobRes = await Axios.get(url, { responseType: 'blob' })
        const blob = new Blob([blobRes.data], { type: mimeType })
        await downloadBlob(blob, fileName)
      } catch (err) {
        enqueueSnackbar('Something went wrong. Please try again later.', {
          variant: 'error',
        })
      }
    },
    []
  )

  const downloadButton = React.useMemo(() => {
    if (
      !downloadableCreative ||
      !creativeDetails ||
      creativeDetails.creativeTypeId === HTML_CREATIVE_ID
    ) {
      return null
    }

    return (
      <IconButton
        onClick={() => {
          handleMediaDownload(
            downloadableCreative,
            creativeDetails?.creativeName || 'creative'
          )
        }}
        color="secondary"
      >
        <Download />
      </IconButton>
    )
  }, [downloadableCreative, creativeDetails, handleMediaDownload])

  const { width, height } = creativeDetails || {}
  return (
    <Box className={styles.visualPreview} sx={{ boxShadow: 200 }}>
      <Box className={styles.buttonRow}>
        {allowSizeChange && creativeDetails?.creativeTypeId && !isNative && (
          <Tooltip
            title={displayActualSize ? 'Fit to height' : 'Show in actual size'}
            arrow
            placement="top"
          >
            <IconButton
              onClick={() => setDisplayActualSize(!displayActualSize)}
              color="secondary"
            >
              {displayActualSize ? <ArrowsMinimize /> : <ArrowsMaximize />}
            </IconButton>
          </Tooltip>
        )}
        {downloadButton}
      </Box>
      <Box
        className={`${styles.previewContent} ${
          displayActualSize && creativeData ? styles.withScroll : ''
        }`}
        style={{
          ...(displayActualSize && height && height < PREVIEW_HEIGHT
            ? { alignItems: 'center' }
            : {}),
          ...(displayActualSize && width && width < PREVIEW_WIDTH
            ? { justifyContent: 'center' }
            : {}),
        }}
      >
        {renderPreview}
      </Box>
    </Box>
  )
}
